/* ==========================================================================
   Cart free-delivery progress bar

   The drawer keeps itself honest without help: Dawn's cart-drawer.js replaces
   the whole of #CartDrawer with freshly rendered Liquid on every cart change,
   so the bar inside it is re-rendered with correct numbers.

   The cart PAGE does not. Dawn's cart.js only swaps the `.js-contents` of
   main-cart-items and main-cart-footer, and the bar deliberately sits outside
   those (it belongs under the page title, above the table, the way the
   reference lays it out). So this file exists to keep the page copy in sync,
   and updates the drawer too — harmless, because it computes the same numbers
   Liquid just rendered.

   Money is formatted from shop.money_with_currency_format, passed down on the
   element, rather than Intl.NumberFormat: Intl would print "PKR 5,000.00"
   while the subtotal three lines below prints "5,000.00 PKR", and two
   different renderings of the same currency in one drawer looks like a bug.
   ========================================================================== */

(() => {
  const SELECTOR = '[data-resemble-progress]';

  const PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/;

  const groupDigits = (digits, separator) =>
    digits.replace(/(\d)(?=(\d\d\d)+$)/g, '$1' + separator);

  /* Shopify's classic money format: a template string with one placeholder
     whose name encodes the decimal count and the two separators. */
  const formatMoney = (cents, format) => {
    const value = Math.max(0, cents) / 100;
    const match = format && format.match(PLACEHOLDER);
    if (!match) return value.toFixed(2);

    const placeholder = match[1];
    const decimals = placeholder.includes('no_decimals') ? 0 : 2;

    let thousands = ',';
    let decimal = '.';
    if (placeholder.includes('comma_separator')) {
      thousands = '.';
      decimal = ',';
    } else if (placeholder.includes('apostrophe_separator')) {
      thousands = "'";
    } else if (placeholder.includes('space_separator')) {
      thousands = ' ';
      decimal = ',';
    }

    const parts = value.toFixed(decimals).split('.');
    const amount =
      groupDigits(parts[0], thousands) + (parts[1] ? decimal + parts[1] : '');

    return format.replace(PLACEHOLDER, amount);
  };

  const update = (cart) => {
    document.querySelectorAll(SELECTOR).forEach((element) => {
      const threshold = Number(element.dataset.threshold || 0);
      if (!threshold) return;

      const remaining = Math.max(threshold - cart.total_price, 0);
      const percent = Math.min((cart.total_price / threshold) * 100, 100);

      element.style.setProperty('--cart-progress', `${percent}%`);
      element.toggleAttribute('data-complete', remaining === 0);

      const amount = element.querySelector('[data-resemble-progress-remaining]');
      if (amount) {
        amount.textContent = formatMoney(remaining, element.dataset.moneyFormat);
      }
    });
  };

  const sync = async () => {
    if (!document.querySelector(SELECTOR)) return;

    try {
      const response = await fetch(`${routes.cart_url}.js`, {
        credentials: 'same-origin',
      });
      update(await response.json());
    } catch (error) {
      /* Leave the server-rendered numbers in place: stale by one interaction
         beats a bar that empties itself because a fetch failed. */
      console.error('Cart progress failed to sync', error);
    }
  };

  if (typeof subscribe === 'function') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      /* Let Dawn finish replacing the sections it owns before reading the
         cart back, so the bar is not updated against markup about to be
         thrown away. */
      window.setTimeout(sync, 120);
    });
  }
})();